"use client";

import {
  forwardRef,
  ComponentPropsWithoutRef,
  ComponentRef,
  CSSProperties,
} from "react";
import * as ProgressPrimitive from "@radix-ui/react-progress";
import { cn } from "@drpcrd/utils/shared";

/**
 * Progress bar component for displaying task completion status. Built on Radix
 * UI primitives with smooth animations and accessibility support.
 *
 * @example
 * ```tsx
 * import { Progress } from '@drpcrd/shared/ui';
 *
 * <Progress value={60} />
 * ```
 */
export const Progress = forwardRef<
  ComponentRef<typeof ProgressPrimitive.Root>,
  ComponentPropsWithoutRef<typeof ProgressPrimitive.Root> & {
    indicatorClassName?: string;
    indicatorStyle?: CSSProperties;
  }
>(({ className, value, indicatorClassName, indicatorStyle, ...props }, ref) => (
  <ProgressPrimitive.Root
    ref={ref}
    className={cn(
      "relative h-2 w-full overflow-hidden rounded-full bg-neutral-200",
      className,
    )}
    {...props}
  >
    <ProgressPrimitive.Indicator
      className={cn(
        "h-full w-full flex-1 bg-neutral-900 transition-all",
        indicatorClassName,
      )}
      style={{
        transform: `translateX(-${100 - (value || 0)}%)`,
        ...indicatorStyle,
      }}
    />
  </ProgressPrimitive.Root>
));
Progress.displayName = ProgressPrimitive.Root.displayName;
